// SIGN OFF A RUN — #553 D5, Session 79.15e.
//
// The one control that closes #553's headline. A fabricator at Houston says how
// many frames they made; the number lands on Houston's own rack, the transfer
// moves from "To make" to "To load", and a truck can finally be filled with
// something that exists.
//
// WHY THIS IS ITS OWN SHEET RATHER THAN A FIELD ON THE SHIP SCREEN. Making and
// loading are done by different people on different days — Houston makes 40 on
// Monday and 60 on Thursday, and a driver fills a truck whenever one is going.
// #517's D64 is explicit that the driver is never asked how a line was filled,
// because that is a fabrication fact and the driver loads a trailer.
//
// AND IT IS A QUANTITY, NEVER A TICK. A "production complete" checkbox is false
// all week while 40 finished frames sit on the floor invisible to the loading
// queue, which is the defect wearing a different hat. It accrues, exactly as
// qty_shipped accrues over several loads.
import React, { useMemo, useState } from 'react';
import Overlay from '../../components/Overlay';
import ActionButton from '../../components/ActionButton';
import { useTier, usesPhoneLayout, controlHeight } from '../../lib/tiers';
import { signOffProduction, isOwnShelf, plainTransferRefusal } from '../../lib/branchTransfers';
import { qtyBox, QTY_BOX_CLASS } from '../../lib/qtyBox';

export default function SignOffRunSheet({
  visible,
  onClose,
  onSaved,
  transfer,
  branchNames = {},
  // #641, Session 79.22b -- the page's own item-name map. The chooser read a
  // name off the LINE, which the page deliberately fetches without the item
  // join (one batched name fetch rather than one per row), so every option
  // fell back to "Item 2", "Item 3" -- database ids nobody in the building
  // has ever seen, on the sheet that decides which stake's count goes up.
  items = {},
}) {
  // TIER, NOT `isMobile` — gate F4, and D24 as Colby corrected it. The app's
  // older mobile flag flips at 768, which would hand a carried iPad a centre
  // dialog; the sheet/dialog boundary is 1024, because a tablet is held in the
  // hand and reaches from the bottom edge like a phone.
  //
  // ABOVE THE EARLY RETURN, deliberately. A `useTier()` sitting after
  // `if (!visible) return null` is a rules-of-hooks ERROR that breaks the BUILD
  // while every unit test passes — 79.15b did exactly that with 1,892 green.
  const { tier, coarse } = useTier();
  const phoneish = usesPhoneLayout(tier);
  // #581 as amended at 79.21b: a button that commits is 48px with a finger.
  const commitH = controlHeight(coarse, 'commit');
  const minH = controlHeight(coarse, 'min');
  const lines = useMemo(
    () => (transfer?.branch_transfer_items || [])
      .filter(l => Math.max(Number(l.qty_requested || 0) - Number(l.qty_ready || 0), 0) > 0),
    [transfer],
  );

  const [lineId, setLineId] = useState(null);
  const [qty, setQty] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [done, setDone] = useState(null);

  const line = lines.find(l => String(l.id) === String(lineId)) || lines[0] || null;
  // #641 -- the name every other label on the Transfers page already uses; the
  // id stays only as the fallback for an item genuinely unknown.
  const nameOf = (l) => items[String(l?.item_id)]?.name || l?.inventory_items?.name || `Item ${l?.item_id}`;
  const requested = Number(line?.qty_requested || 0);
  const ready = Number(line?.qty_ready || 0);
  const outstanding = Math.max(requested - ready, 0);

  // DEFAULTS TO THE WHOLE OUTSTANDING AMOUNT, because finishing the job is the
  // common case and a person who made fewer edits one number down. Gate F3.
  const typed = qty === '' ? outstanding : Number(qty);
  const over = Number.isFinite(typed) && typed > outstanding;
  const nothing = !Number.isFinite(typed) || typed <= 0;
  // #723, Session 79.22b.4.2.1 -- what is left once this run is signed off.
  // At 0 the run finishes the request, and the sheet said "0 left after this.
  // You can sign off again tomorrow." -- a tomorrow with nothing in it.
  const leftAfter = Math.max(outstanding - (Number.isFinite(typed) ? typed : 0), 0);

  const branchName = branchNames[String(transfer?.from_branch_id)] || 'the branch';
  // #580 part (a), gate 553 D21 -- made for this branch's own shelf: every run
  // lands on the shelf as it is signed off, nothing is ever loaded, and the
  // last run finishes the request. "Become loadable" would be untrue here.
  const own = isOwnShelf(transfer);
  // #665 D14 -- who the run is for, named beside the transfer's number so the
  // number never stands alone (371 D9/D20).
  const forWhom = own
    ? `${branchName}’s shelf`
    : (branchNames[String(transfer?.to_branch_id)] || 'the branch that asked');

  // aria-disabled with a VISIBLE reason, never a disabled attribute — #492's
  // ruling. A disabled button somebody taps still does nothing, and on a phone
  // there is no hover to reveal why.
  const blockedReason =
    !line ? 'Everything on this transfer has been signed off.'
      : nothing ? 'Say how many you made.'
        : over ? `Only ${outstanding} were asked for. Sign off ${outstanding} or fewer.`
          : null;

  async function submit() {
    if (blockedReason || busy || !line) return;
    setBusy(true);
    setErr(null);
    // #665 D7 -- the page's name map rides along, so the bell names the item.
    const r = await signOffProduction(line, typed, { transfer, branchNames, items });
    setBusy(false);
    if (r.error) {
      // INLINE, BESIDE THE CONTROL THAT FAILED, and the typed number survives.
      // #499's work-order number was refused by a constraint, surfaced nowhere,
      // and the count the person had typed was lost.
      // #585 -- in plain words; and when somebody else signed off first, the
      // page behind the sheet reloads so the numbers it shows are true again.
      const plain = plainTransferRefusal(r.error.message || '');
      setErr(plain.text);
      // #675 D22, Grok's review at 79.22b.5.5b -- said as a race, so the desk's
      // right side does not answer it as this person's sign-off.
      if (plain.race) onSaved?.({ race: true });
      return;
    }
    // RENDERS WHAT THE DATABASE ENDED UP WITH, not what was typed. `error ===
    // null` is not proof a write happened — 79.1's packing-slip control said
    // "Added just now" and wrote nothing.
    setDone({ signed: typed, readyNow: r.readyNow, finished: r.finished === true, name: nameOf(line) });
    onSaved?.();
  }

  function close() {
    setQty('');
    setErr(null);
    setDone(null);
    setLineId(null);
    onClose?.();
  }

  if (!visible) return null;

  const label = { fontSize: 12, fontWeight: 700, color: '#3A3A3A', display: 'block', marginBottom: 5 };

  return (
    <Overlay variant={phoneish ? 'sheet' : 'modal'} onClose={close} maxWidth={440}>
      <div style={{ padding: 16 }}>
        <div style={{ fontSize: 16, fontWeight: 800, color: '#0F0F0F' }}>Sign off a run</div>

        {done ? (
          <div style={{ marginTop: 12 }}>
            <div style={{ fontSize: 13, color: '#14503B', fontWeight: 700 }}>
              {/* #641 -- the confirmation names what was signed off, so the maker
                  can see which stake's count just moved. */}
              {done.signed} {done.name} signed off.
            </div>
            <div style={{ marginTop: 6, fontSize: 12, color: '#3A3A3A' }}>
              {own
                ? (done.finished
                  // 553 F2's own words for the finish.
                  ? `All ${requested} are on ${branchName}\u2019s shelf. This request is finished.`
                  : `${done.readyNow} of ${requested} are on ${branchName}\u2019s shelf \u00b7 ${Math.max(requested - done.readyNow, 0)} still to make.`)
                : `${done.readyNow} of ${requested} are now standing at ${branchName} and ready to load.`}
            </div>
            <button
              onClick={close}
              style={{
                marginTop: 14, width: '100%', minHeight: minH, borderRadius: 8,
                border: '1px solid #D8D8D8', background: '#fff', fontWeight: 700, cursor: 'pointer',
              }}
            >Done</button>
          </div>
        ) : !line ? (
          <div style={{ marginTop: 12, fontSize: 13, color: '#5C5C5C' }}>
            Everything on this transfer has been signed off. Nothing left to make.
          </div>
        ) : (
          <>
            {lines.length > 1 && (
              <div style={{ marginTop: 12 }}>
                <label style={label} htmlFor="signoff-line">Which item?</label>
                <select
                  id="signoff-line"
                  value={String(line.id)}
                  onChange={e => { setLineId(e.target.value); setQty(''); setErr(null); }}
                  style={{ width: '100%', minHeight: 44, fontSize: 16, borderRadius: 8, border: '1px solid #D8D8D8', padding: '0 10px' }}
                >
                  {lines.map(l => (
                    <option key={l.id} value={String(l.id)}>
                      {nameOf(l)}
                      {' · '}
                      {Math.max(Number(l.qty_requested || 0) - Number(l.qty_ready || 0), 0)} to make
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* #665 D14 -- THE ITEM BEFORE THE NUMBER, on a one-item transfer
                too. The name appeared only in the confirmation afterwards, so
                the fabricator learned what he had signed off once he had said
                he made it. With several items the chooser stays above (#641)
                and this follows whatever is chosen. */}
            <div style={{ marginTop: 12 }}>
              <div style={{ fontSize: 15, fontWeight: 700, color: '#0F0F0F' }}>{nameOf(line)}</div>
              <div style={{ marginTop: 2, fontSize: 12, color: '#3A3A3A' }}>
                {outstanding} still to make of {requested}
                {transfer?.transfer_number ? ` · ${transfer.transfer_number} for ${forWhom}` : ` · for ${forWhom}`}
              </div>
            </div>

            <div style={{ marginTop: 12 }}>
              {/* 16px minimum on every text entry — locked rule 12, iOS
                  auto-zoom. inputMode numeric brings up the number pad without
                  a type=number spinner on desktop. */}
              <label style={label} htmlFor="signoff-qty">How many did you make?</label>
              <input
                id="signoff-qty"
                inputMode="numeric"
                value={qty}
                placeholder={String(outstanding)}
                onChange={e => { setQty(e.target.value.replace(/[^\d.]/g, '')); setErr(null); }}
                // #708 -- the count this sign-off confirms (blank signs off the
                // one shown), drawn with the shared confirm-box style.
                className={QTY_BOX_CLASS}
                style={qtyBox({ width: '100%', align: 'left' })}
              />
              <div style={{ marginTop: 5, fontSize: 12, color: '#5C5C5C' }}>
                {over
                  ? `Only ${outstanding} were asked for. Sign off ${outstanding} or fewer.`
                  : leftAfter === 0
                    ? `That makes all ${requested} asked for.`
                    : `${leftAfter} left after this. You can sign off again tomorrow.`}
              </div>
            </div>

            {/* SAYS WHAT HAPPENS, AND NAMES THE BRANCH. A sentence that reads
                "onto the rack" is true from one side of the company and
                meaningless from the other — the same shape as the message that
                told Dallas there was no shelf to check while Houston held 40
                frames. It also says what does NOT happen, because signing off
                is not shipping and the two are a day apart. */}
            {!nothing && !over && (
              <div style={{
                marginTop: 12, padding: 10, borderRadius: 8,
                background: '#F1FAF6', border: '1px solid #CFE9DE',
                fontSize: 12, color: '#14503B',
              }}>
                {own
                  ? <>These {typed} {nameOf(line)} go onto {branchName}&rsquo;s shelf now. Nothing is loaded &mdash; this was asked for our own shelf{typed === outstanding ? ', and this finishes it.' : '.'}</>
                  : <>These {typed} {nameOf(line)} go onto {branchName}&rsquo;s rack and become loadable. Nothing leaves the branch yet.</>}
              </div>
            )}

            {err && (
              <div role="alert" style={{
                marginTop: 12, padding: 10, borderRadius: 8,
                background: '#FDF1F1', border: '1px solid #F0CFCF',
                fontSize: 12, color: '#8E2F2F', fontWeight: 600,
              }}>{err}</div>
            )}

            <div style={{ marginTop: 14, display: 'flex', gap: 8 }}>
              <button
                onClick={close}
                style={{
                  flex: '0 0 auto', minHeight: minH, padding: '0 14px', borderRadius: 8,
                  border: '1px solid #D8D8D8', background: '#fff', fontWeight: 600, cursor: 'pointer',
                }}
              >Cancel</button>
              <ActionButton
                onClick={submit}
                blockedReason={blockedReason}
                reasonAlreadyShown
                busy={busy}
                busyLabel="Signing off…"
                style={{
                  flex: 1, minHeight: commitH, borderRadius: 8, border: 0,
                  background: '#14503B', color: '#fff', fontWeight: 800, cursor: 'pointer',
                }}
              >
                {/* #665 D14 -- the button names what it signs off. */}
                {nothing || over ? 'Sign off' : `Sign off ${typed} ${nameOf(line)}`}
              </ActionButton>
            </div>
          </>
        )}
      </div>
    </Overlay>
  );
}
